/**
 * Why a proposal is what it is, in the words the shop reads (contract v10.30).
 *
 * The pure half of the panel's «porqué»: `TriageDecisionRead` now says what the proposal
 * rests on (`basis`), what the sender memory changed (`learned_*`), how many answered
 * emails the model saw (`similar_examples`) and why the system is not sure
 * (`doubt_reasons`). Here those become sentences, so the tests can pin the words without
 * rendering and the panel only lays them out.
 *
 * Two rules run through all of it. Nothing here changes where an email goes: a doubt is
 * a notice to look at it, never a new destination. And nothing is jargon: the contract
 * calls the reasons «vecinos» and «segunda_opinion», and Víctor reads «no coincide con
 * correos parecidos ya revisados».
 */
import {
  CATEGORY_LABELS,
  CONFIDENCE_FLOOR,
  DESTINATION_LABELS,
  type TriageDestination,
} from '@/shared/triage/vocabulary';

import { formatDayMonth } from './dates';
import type { DoubtReason, TriageBasis, TriageDecision } from './types';

/** The reasons in the order they are told, whatever order they arrived in. */
const DOUBT_ORDER: readonly DoubtReason[] = ['vecinos', 'segunda_opinion', 'confianza_baja'];

/**
 * The doubt rule of the mailbox took the destination (`confianza_baja`). It says where the
 * email went, not that anyone must look at it, so it has its own line.
 */
export const UNSURE_TEXT =
  'El sistema no está seguro: el destino es la regla de duda del buzón, no la de la categoría.';

/**
 * The two doubts that ask for a review, as the end of «Revísalo: …». Each is a complete
 * sentence on its own, so it reads the same alone and next to the other one.
 */
const REVIEW_TEXT: Partial<Record<DoubtReason, string>> = {
  vecinos: 'no coincide con correos parecidos ya revisados',
  segunda_opinion: 'el sistema lo leyó dos veces y no le salió lo mismo',
};

/**
 * Why the system is not sure, from the back when it says and from the confidence when it
 * does not.
 *
 * A back that sends `doubt_reasons` (v10.30 on) is the authority, `confianza_baja`
 * included: it does not raise it for a proposal the sender memory or the bulk footer
 * decided, because neither is the model's answer and the mailbox's doubt rule did not
 * pick the destination. Reading the confidence on top of that would print «el destino es
 * la regla de duda» over a destination the memory chose. A back older than v10.30 sends
 * no such field, and there the confidence under the floor is all there is, as before.
 * Values this front does not know are dropped: a new reason of the back must not print
 * a slug at somebody.
 */
export function doubtReasons(decision: TriageDecision): DoubtReason[] {
  if (decision.doubt_reasons !== undefined) {
    const given = new Set<string>(decision.doubt_reasons);
    return DOUBT_ORDER.filter((reason) => given.has(reason));
  }
  const confidence = decision.confidence ?? null;
  return confidence !== null && confidence < CONFIDENCE_FLOOR ? ['confianza_baja'] : [];
}

/** The doubts that ask for a review, as sentence endings, in telling order. */
export function reviewTexts(reasons: readonly DoubtReason[]): string[] {
  return reasons.flatMap((reason) => REVIEW_TEXT[reason] ?? []);
}

/**
 * What something learned did to this proposal: the sender memory (`basis = memory`, the
 * decisions made before ADR 0030 of the back), a criterion from a note (`guideline`) or an
 * answered email of the same sender (`precedent`), contract v10.35.
 */
export interface LearnedLine {
  /** «Como se corrigió el 29/09 un correo de este remitente», «Criterio de Víctor (25/09): …». */
  sentence: string;
  /** «Sin eso: Otro · Se queda en el buzón»; null when the back could not say. */
  without: string | null;
  /** Whether `sentence` carries the date, which is what lets it stand in for the back's own. */
  dated: boolean;
}

/** The memory line has the same shape as the other two. */
export type MemoryNote = LearnedLine;

/** The precedent line, with the email it followed when the back names it. */
export interface PrecedentNote extends LearnedLine {
  /** The answered email whose destination this one followed (`precedent_email_id`). */
  emailId: string | null;
}

/** The destination as the panel says it: «Va a Paulo», «Se queda en el buzón». */
function destinationPhrase(destination: TriageDestination): string {
  return destination === 'se_queda'
    ? `${DESTINATION_LABELS[destination]} en el buzón`
    : `Va a ${DESTINATION_LABELS[destination]}`;
}

/** «Sin eso: Otro · Se queda en el buzón», from `learned_from_*`; null when the back sent neither. */
function withoutText(decision: TriageDecision): string | null {
  const parts: string[] = [];
  if (decision.learned_from_category) {
    parts.push(CATEGORY_LABELS[decision.learned_from_category]);
  }
  if (decision.learned_from_destination) {
    parts.push(destinationPhrase(decision.learned_from_destination));
  }
  return parts.length > 0 ? `Sin eso: ${parts.join(' · ')}` : null;
}

/**
 * The memory line: the day the last correction was made and what the system would have
 * proposed without it. Null unless the memory decided.
 *
 * Built from the fields and not read out of `rationale`, because the fields are the
 * contract and the sentence is the back's wording of the day. The date is the wall clock
 * of the shop (`formatDayMonth`), the same day the back writes in its own sentence, which
 * it computes in Madrid; they can only differ for a browser outside Spain.
 */
export function memoryNote(decision: TriageDecision, now: Date = new Date()): MemoryNote | null {
  if (decision.basis !== 'memory') return null;
  const day = decision.learned_at ? formatDayMonth(decision.learned_at, now) : '';
  return {
    sentence: day
      ? `Como se corrigió el ${day} un correo de este remitente`
      : 'Como se corrigió antes un correo de este remitente',
    without: withoutText(decision),
    dated: day !== '',
  };
}

/**
 * The criterion that changed the proposal (`basis = guideline`, contract v10.35): «Criterio
 * de Víctor (25/09): Todos los pedidos de Celsa van a María», and in small print what it
 * would have been without it. The last of `applied_guidelines`, which the back lists
 * oldest first and whose last one is the one its own rationale names; the day is when the
 * note was written. Null unless a criterion decided.
 */
export function guidelineNote(
  decision: TriageDecision,
  now: Date = new Date(),
): LearnedLine | null {
  if (decision.basis !== 'guideline') return null;
  const last = decision.applied_guidelines?.at(-1);
  const when = last?.created_at ?? decision.learned_at ?? null;
  const day = when ? formatDayMonth(when, now) : '';
  const who = last?.author ? `de ${last.author}` : 'aprendido';
  const dated = day ? ` (${day})` : '';
  return {
    sentence: last
      ? `Criterio ${who}${dated}: ${last.text}`
      : `Lo ha cambiado un criterio aprendido de una nota${dated}`,
    without: withoutText(decision),
    dated: day !== '',
  };
}

/**
 * The answered email this one followed (`basis = precedent`, contract v10.35): «Como en un
 * correo parecido de este remitente respondido el 29/09», with that email when the back
 * names it. The model judged both the same kind of email; the destination is the one a
 * person gave the other. Null unless a precedent decided.
 */
export function precedentNote(
  decision: TriageDecision,
  now: Date = new Date(),
): PrecedentNote | null {
  if (decision.basis !== 'precedent') return null;
  const day = decision.learned_at ? formatDayMonth(decision.learned_at, now) : '';
  return {
    sentence: day
      ? `Como en un correo parecido de este remitente respondido el ${day}`
      : 'Como en un correo parecido de este remitente ya respondido',
    without: withoutText(decision),
    dated: day !== '',
    emailId: decision.precedent_email_id ?? null,
  };
}

/**
 * «Coincide con el criterio de Víctor (25/09): …», when a criterion bore on the proposal
 * without changing it (the model or a rule already said the same; the back lists it in
 * `applied_guidelines` with `basis` still `model` or `rule`). Quiet: it explains, it did
 * not decide.
 */
export function agreesText(decision: TriageDecision, now: Date = new Date()): string | null {
  if (decision.basis !== 'model' && decision.basis !== 'rule') return null;
  const last = decision.applied_guidelines?.at(-1);
  if (!last) return null;
  const day = last.created_at ? ` (${formatDayMonth(last.created_at, now)})` : '';
  const who = last.author ? `el criterio de ${last.author}` : 'un criterio aprendido';
  return `Coincide con ${who}${day}: ${last.text}`;
}

/**
 * The sentence the back writes into `rationale` when the memory decides («Como la última
 * vez que se corrigió un correo de este remitente (29/09): pedido con albarán, a Paulo.»,
 * `cubas.triage.memory.explain`). The second person is the older wording of the same line.
 */
const MEMORY_SENTENCE =
  /^Como la última vez que (?:se corrigió|corregiste) un correo de este remitente(?: \([^)]*\))?:[^.]*\.\s*/i;

/**
 * The back's sentence when a precedent decided («Como en un correo parecido de este
 * remitente que se respondió el 29/09: pedido con albarán, a Paulo.», `cubas.triage.learned
 * .explain`). The category and the destination are the proposal, already on screen.
 */
const PRECEDENT_SENTENCE =
  /^Como en un correo parecido de este remitente que se respondió el [^:]*:[^.]*\.\s*/i;

/** The opening of the back's sentence when a criterion decided: «Criterio de Víctor (25/09): ». */
const GUIDELINE_OPENING = /^Criterio (?:de [^(:]+?|aprendido)(?: \([^)]*\))?: /i;

/**
 * The line under the destination that gives the reason, or null when there is none.
 *
 * When something learned decided (the memory, a criterion, a precedent), the back's
 * sentence in the rationale says the same as the panel's own line, which is built from
 * the fields, so the back's is dropped. What the back appended after it (an attachment
 * the provider could not open) stays. If the sentence is worded some other way, or the
 * panel's line has no date to stand in for it, the rationale is shown whole: a repeated
 * line costs a second of reading, a lost one costs the reason. A criterion's sentence goes
 * whenever it names the criterion the panel names: the panel's line carries its text.
 */
export function rationaleText(
  decision: TriageDecision,
  learned: LearnedLine | null,
): string | null {
  const rationale = decision.rationale?.trim() ?? '';
  if (!rationale) return null;
  if (decision.basis === 'guideline' && learned) {
    const text = decision.applied_guidelines?.at(-1)?.text ?? '';
    const opening = GUIDELINE_OPENING.exec(rationale);
    if (!opening || !text) return rationale;
    const rest = rationale.slice(opening[0].length);
    if (!rest.startsWith(text)) return rationale;
    return rest.slice(text.length).trim() || null;
  }
  if (!learned?.dated) return rationale;
  const sentence = decision.basis === 'precedent' ? PRECEDENT_SENTENCE : MEMORY_SENTENCE;
  return rationale.replace(sentence, '').trim() || null;
}

/** Wording the rationale of a bulk-footer decision already has: then the panel adds nothing. */
const SAYS_BULK = /env[ií]o masivo|pie de env[ií]o|darse de baja/i;

/**
 * «Tiene pie de envío masivo: se trata como publicidad.», only when the code moved the
 * proposal to advertising because of the footer and the rationale does not say so. The
 * back's own reason («Es un envío masivo: trae el enlace para darse de baja…») does, so
 * in practice this fills the gap of a decision stored without one.
 */
export function bulkFooterText(decision: TriageDecision): string | null {
  if (decision.basis !== 'bulk_footer') return null;
  if (SAYS_BULK.test(decision.rationale ?? '')) return null;
  return 'Tiene pie de envío masivo: se trata como publicidad.';
}

/** The bases where the answer on screen is not the model's: something learned or the footer. */
const LEARNED_OR_FOOTER = new Set<TriageBasis>(['memory', 'guideline', 'precedent', 'bulk_footer']);

/**
 * «Decidido viendo 2 correos parecidos ya revisados», when the model was shown
 * answered emails. Not when something learned or the bulk footer decided: the model saw
 * them, but its answer is not the one on screen, and «decidido viendo» would be false.
 */
export function examplesText(decision: TriageDecision): string | null {
  const seen = decision.similar_examples ?? 0;
  if (seen <= 0 || (decision.basis && LEARNED_OR_FOOTER.has(decision.basis))) return null;
  return seen === 1
    ? 'Decidido viendo 1 correo parecido ya revisado'
    : `Decidido viendo ${seen} correos parecidos ya revisados`;
}
